import { useContext } from "react";
import axios from "axios";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useHistory } from "react-router-dom";

import { SettingsContext } from "../../contexts";

export const useQuotes = (type, requestId, productId, fetchLimit = 10) => {
  return useInfiniteQuery({
    queryKey: ["quotes", requestId],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await axios.post(`/api/quotes/list`, {
        filters: {
          product_id: productId,
          request_id: requestId,
          type,
        },
        sort: {
          field: "created_at",
          direction: "desc",
        },
        paging: {
          offset: (pageParam - 1) * fetchLimit,
          limit: fetchLimit,
        },
      });

      if (response.data?.items.length === 0) {
        return undefined;
      }

      return response.data.items;
    },
    getNextPageParam: () => {
      return { pageParam: 1 };
    },
    staleTime: 5 * 60 * 1000,
  });
};

const QUOTE_STALE_TIME = 30 * 1000;

const fetchQuote = async (quoteId) => {
  if (quoteId === "new" || quoteId === "select-listing" || !quoteId) {
    return null;
  }

  const response = await axios.get(`/api/quotes/${quoteId}`);

  return response.data;
};

/**
 * Starts loading a quote before it is opened (the quote list calls it when a
 * row is pointed at / focused), so a click opens it without a wait. Same key
 * and fetch as useQuote; a fresh copy is not fetched again.
 */
export const usePrefetchQuote = () => {
  const queryClient = useQueryClient();

  return (quoteId) =>
    queryClient.prefetchQuery({
      queryKey: ["quote", quoteId],
      queryFn: () => fetchQuote(quoteId),
      staleTime: QUOTE_STALE_TIME,
    });
};

export const useQuote = (quoteId) => {
  const query = useQuery({
    queryKey: ["quote", quoteId],
    queryFn: () => fetchQuote(quoteId),
    staleTime: QUOTE_STALE_TIME,
  });

  return {
    quote: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

export const useUpsertQuote = () => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const settings = useContext(SettingsContext);

  const query = useMutation({
    mutationFn: async ({ id, quoteData }) => {
      let response;
      if (id !== "new" && id !== "select-listing") {
        quoteData.id = id;
        response = await axios.put(`/api/quotes/${id}`, quoteData);
      } else {
        response = await axios.post(`/api/quotes`, quoteData);
      }

      return response.data;
    },
    onSuccess: (data, { id, navigateOnSave }) => {
      // Always invalidate the request's quote LIST so a newly created/updated
      // quote shows up under SELECT QUOTE. Previously only ["quote", id] (the
      // single quote) was invalidated, so the cached (often empty) list query
      // ["quotes", requestId] persisted for its 5-min staleTime.
      if (data?.request_id) {
        queryClient.invalidateQueries({
          queryKey: ["quotes", data.request_id],
          refetchType: "all",
        });
      }

      const type = history.location.pathname
        .split("/")
        .slice(1, 3)
        .join("/");
      if (
        (id === "new" || id === "select-listing") &&
        navigateOnSave &&
        type === "requests/orders"
      ) {
        const requestId = data.request_id;
        history.push(
          `/${type}/${requestId}/${data.id}?${settings.iframeQueryParams}`
        );

        return;
      }

      queryClient.invalidateQueries({
        queryKey: ["quote", parseInt(id, 10)],
        refetchType: "all",
      });
    },
  });

  return {
    upsertQuote: query.mutateAsync,
    status: query.status,
    error: query.error,
  };
};

export const useApproveQuote = () => {
  const queryClient = useQueryClient();

  const query = useMutation({
    mutationFn: async (quoteId) => {
      const response = await axios.put(`/api/quotes/${quoteId}/approve`);

      return response.data;
    },
    onSuccess: (data, quoteId) => {
      queryClient.invalidateQueries({
        queryKey: ["quote", parseInt(quoteId, 10)],
        refetchType: "all",
      });
    },
  });

  return {
    approveQuote: query.mutateAsync,
    status: query.status,
    error: query.error,
  };
};

export const useAssignReviewer = () => {
  const queryClient = useQueryClient();

  const query = useMutation({
    mutationFn: async ({ quoteId, reviewerId }) => {
      const response = await axios.put(`/api/quotes/${quoteId}/reviewer`, {
        reviewer_id: reviewerId,
      });

      return response.data;
    },
    onSuccess: (data, { quoteId }) => {
      queryClient.invalidateQueries({
        queryKey: ["quote", parseInt(quoteId, 10)],
        refetchType: "all",
      });
    },
  });

  return {
    assignReviewer: query.mutateAsync,
    status: query.status,
    error: query.error,
  };
};

export const useCreateListing = () => {
  const queryClient = useQueryClient();
  const settings = useContext(SettingsContext);

  const query = useMutation({
    mutationFn: async (quoteId) => {
      const response = await axios.post(
        `/api/quotes/${quoteId}/create-listing`
      );

      return response.data;
    },
    onSuccess: (data, quoteId) => {
      queryClient.invalidateQueries({
        queryKey: ["quote", parseInt(quoteId, 10)],
        refetchType: "all",
      });

      // Open the listing edit in a new tab so admins keep their place
      // in the request edit flow. Do not fall back to same-tab nav on a
      // null return value — Safari and noopener cause window.open to
      // return null even when the popup succeeded, which previously
      // caused the page to also redirect, opening both tabs.
      const url = `/product/${data.listing_id}/edit/listings?${settings.iframeQueryParams}`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
  });

  return {
    createListing: query.mutateAsync,
    status: query.status,
    error: query.error,
  };
};

export const useUpdateListing = () => {
  const queryClient = useQueryClient();
  const settings = useContext(SettingsContext);

  const query = useMutation({
    mutationFn: async (quote) => {
      const response = await axios.put(
        `/api/quotes/${quote.id}/update-listing`,
        { quote }
      );

      return response.data;
    },
    onSuccess: (data, quoteId) => {
      queryClient.invalidateQueries({
        queryKey: ["quote", parseInt(quoteId, 10)],
        refetchType: "all",
      });

      const url = `/product/${data.listing_id}/edit/listings?${settings.iframeQueryParams}`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
  });

  return {
    updateListing: query.mutateAsync,
    status: query.status,
    error: query.error,
  };
};

export const useAcceptedByClient = () => {
  const queryClient = useQueryClient();

  const query = useMutation({
    mutationFn: async (quoteId) => {
      const response = await axios.put(`/api/quotes/${quoteId}/accepted`);

      return response.data;
    },
    onSuccess: (data, quoteId) => {
      queryClient.invalidateQueries({
        queryKey: ["quote", parseInt(quoteId, 10)],
        refetchType: "all",
      });
    },
  });

  return {
    acceptedByClient: query.mutateAsync,
    status: query.status,
    error: query.error,
  };
};

export const useSendQuote = () => {
  const queryClient = useQueryClient();

  const query = useMutation({
    mutationFn: async ({ id, type }) => {
      const response = await axios.post(`/api/quotes/${id}/send-quote`, {
        type,
      });

      return response.data;
    },
    onSuccess: (data, quoteId) => {
      queryClient.invalidateQueries({
        queryKey: ["quote", quoteId.id],
        refetchType: "all",
      });
    },
  });

  return {
    sendQuote: query.mutateAsync,
    status: query.status,
    error: query.error,
  };
};

export const useUpdateQuoteType = () => {
  const queryClient = useQueryClient();

  const query = useMutation({
    mutationFn: async ({ id, type }) => {
      const response = await axios.put(`/api/quotes/${id}/quote-type`, {
        type,
      });

      return response.data;
    },
    onSuccess: (data, quoteId) => {
      queryClient.invalidateQueries({
        queryKey: ["quote", quoteId.id],
        refetchType: "all",
      });
    },
  });

  return {
    updateQuoteType: query.mutateAsync,
    status: query.status,
    error: query.error,
  };
};
